import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";

import refreshFileUrl from "../utils/refreshFileUrl";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

import "../utils/pdfWorker";

const Presentation = (props) => {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scale, setScale] = useState(1);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [pdfDimensions, setPdfDimensions] = useState(null);
  const documentContainerRef = useRef(null);

  const { lesson, courseId, handleLessonCompletion, isLastLesson } = props;

  const [fileUrl, setFileUrl] = useState(lesson.file_url);
  const [loadError, setLoadError] = useState(false);
  const retriedRef = useRef(false);

  useEffect(() => {
    setFileUrl(lesson.file_url);
    setLoadError(false);
    retriedRef.current = false;
  }, [lesson.id, lesson.file_url]);

  // Same expiry recovery as Video: refetch a signed URL once, keeping the current page.
  const onDocumentLoadError = async () => {
    if (retriedRef.current) {
      setLoadError(true);
      return;
    }

    retriedRef.current = true;

    const refreshed = await refreshFileUrl(courseId, lesson.id);

    if (refreshed) {
      setFileUrl(refreshed);
    } else {
      setLoadError(true);
    }
  };

  const onDocumentLoadSuccess = (pdfInfo) => {
    setNumPages(pdfInfo.numPages);
  };

  const onPageLoadSuccess = (page) => {
    // Only set PDF dimensions once when first loading
    if (!pdfDimensions) {
      setPdfDimensions({
        width: page.width,
        height: page.height,
      });
    }
  };

  const calculateScale = () => {
    if (documentContainerRef.current && pdfDimensions) {
      const container = documentContainerRef.current;
      const containerWidth = container.clientWidth - 32; // Account for padding
      const containerHeight = container.clientHeight - 100; // Account for controls and padding

      // Calculate scale to fit both width and height
      const scaleX = containerWidth / pdfDimensions.width;
      const scaleY = containerHeight / pdfDimensions.height;
      const newScale = Math.min(scaleX, scaleY, 3); // Cap at 2x for readability

      return {
        scale: newScale,
        dimensions: { width: containerWidth, height: containerHeight },
      };
    }
    return null;
  };

  const previousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const nextPage = () => {
    if (pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handleNextLesson = async () => {
    await handleLessonCompletion();
  };

  const toggleFullscreen = () => {
    if (!isFullscreen && documentContainerRef.current) {
      // Enter fullscreen
      const element = documentContainerRef.current;
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement;

      const isCurrentlyFullscreen =
        fullscreenElement === documentContainerRef.current;
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange,
      );
    };
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        previousPage();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        nextPage();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [pageNumber, numPages]);

  // Calculate scale when PDF dimensions are available or container changes
  useEffect(() => {
    if (pdfDimensions) {
      const result = calculateScale();
      if (result) {
        setScale(result.scale);
        setContainerDimensions(result.dimensions);
      }
    }
  }, [pdfDimensions, isFullscreen]);

  // Handle window resize to recalculate scale
  useEffect(() => {
    const handleResize = () => {
      if (pdfDimensions) {
        const result = calculateScale();
        if (result) {
          // Only update if dimensions actually changed
          if (
            result.dimensions.width !== containerDimensions.width ||
            result.dimensions.height !== containerDimensions.height
          ) {
            setScale(result.scale);
            setContainerDimensions(result.dimensions);
          }
        }
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [pdfDimensions, containerDimensions]);

  return (
    <div className="w-full h-full flex flex-col">
      {loadError && (
        <div className="mb-3 px-4 py-3 bg-gray-100 text-sm text-gray-700">
          Nie udało się wczytać materiału. Odśwież stronę, aby kontynuować.
        </div>
      )}
      <div
        ref={documentContainerRef}
        className={`w-full pb-10 flex-1 flex flex-col ${
          isFullscreen ? "items-center justify-center p-4" : ""
        }`}
      >
        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading="Ładowanie PDF..."
          className={`flex justify-center ${
            isFullscreen ? "h-full w-full items-center" : "flex-1 h-full"
          }`}
        >
          <Page
            pageNumber={pageNumber}
            onLoadSuccess={onPageLoadSuccess}
            scale={scale}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>

        <div
          className={`flex items-center mt-4 ${isFullscreen ? "justify-center space-x-8" : "justify-between"}`}
        >
          <button
            onClick={previousPage}
            disabled={pageNumber <= 1}
            className={`
                px-6 py-2 text-white text-sm
                ${isFullscreen ? "bg-primaryYellow" : "bg-black hover:bg-primaryYellow"}
                disabled:bg-gray-200 disabled:text-gray-400
                enabled:cursor-pointer
                transition-colors
              `}
          >
            Wcześniejszy slajd
          </button>
          {!isFullscreen && (
            <div className="flex items-center space-x-4">
              <p>
                Strona {pageNumber} z {numPages}
              </p>
              <button
                onClick={toggleFullscreen}
                className="
                  px-4 py-2 bg-gray-600 text-white text-sm
                  hover:bg-gray-700
                  cursor-pointer
                  transition-colors
                "
              >
                {isFullscreen ? "Wyjdź z pełnego ekranu" : "Pełny ekran"}
              </button>
            </div>
          )}
          {isFullscreen && (
            <div className="flex items-center space-x-6">
              <p className="text-white bg-black bg-opacity-50 px-3 py-1 rounded">
                Strona {pageNumber} z {numPages}
              </p>
              <button
                onClick={toggleFullscreen}
                className="
                  px-4 py-2 bg-gray-600 text-white text-sm
                  hover:bg-gray-700
                  cursor-pointer
                  transition-colors
                "
              >
                Wyjdź z pełnego ekranu
              </button>
            </div>
          )}
          <button
            onClick={nextPage}
            disabled={pageNumber >= numPages}
            className={`
                px-6 py-2 text-white text-sm
                ${isFullscreen ? "bg-primaryYellow " : "bg-black hover:bg-primaryYellow"}
                disabled:bg-gray-200 disabled:text-gray-400
                enabled:cursor-pointer
                transition-colors
              `}
          >
            Następny slajd
          </button>
        </div>
      </div>
      <div className="flex justify-end">
        <button
          onClick={handleNextLesson}
          disabled={pageNumber != numPages}
          className="
            px-6 py-2 bg-black text-white text-sm
            hover:bg-primaryYellow
            disabled:bg-gray-200 disabled:text-gray-400
            enabled:cursor-pointer
            transition-colors
          "
        >
          {isLastLesson ? "Zakończ kurs" : "Następna lekcja"}
        </button>
      </div>
    </div>
  );
};

Presentation.propTypes = {
  lesson: PropTypes.object,
  courseId: PropTypes.string,
  handleLessonCompletion: PropTypes.func,
  isLastLesson: PropTypes.bool,
};

export default Presentation;
